import ButtonLoader from '@anm/components/buttons/ButtonLoader';
import { ENTER_NAME, ESC_NAME, NUMPAD_ENTER_NAME } from '@anm/constants/keyCodes';
import useDiffSave from '@anm/hooks/useDiffSave';
import { UserFormRow } from '@anm/styles/helpers';
import { Config as FinalFormConfig } from 'final-form';
import { FC, KeyboardEvent } from 'react';
import { Field, Form, FormRenderProps } from 'react-final-form';
import { Omit } from 'types/helpers';

import { UploadAvatarData } from '../../AvatarUpload';
import TitleBlock from '../../TitleBlock';
import FormHelperText from '../../form/FormHelperText';
import UserAvatarInput from '../../form/UserAvatarInput';
import { DisplayNameField, EmailCheckExistField, NameField } from '../../form/fields';
import UserEmailAlert from '../UserEmailAlert';

import { UserFormSection, WrapperUserForm } from './Wrapper';

type HOCs = FinalFormConfig<UserFormFields>;
export type UserFormProps = {
  onAutoSave: (fields: UserFormFields) => void;
  onEmailResend: () => void;
  isPending?: boolean;
  isResendEmailPending?: boolean;
  submitError?: string | null;
  hasEmailWarning?: boolean;
  disabledFields?: Partial<{ [key in keyof Omit<UserFormFields, 'avatar'>]: boolean }>;
};

export type UserFormFields = {
  email: string;
  avatar: UploadAvatarData | string;
  username: string;
  displayName: string;
};

const renderForm = ({
  submitError,
  hasEmailWarning,
  disabledFields,
  onAutoSave,
  isPending,
  isResendEmailPending,
  onEmailResend
}: UserFormProps) => ({ values, form }: FormRenderProps<UserFormFields>) => {
  const { dirty, valid, initialValues } = form.getState();
  const submit = () => {
    valid && onAutoSave(values);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if ([ENTER_NAME, NUMPAD_ENTER_NAME].includes(event.code)) {
      event.preventDefault();
      submit();
    }
    if (event.code === ESC_NAME) form.reset();
  };

  return (
    <WrapperUserForm onSubmit={event => event.preventDefault()} onKeyDown={handleKeyDown}>
      <UserFormSection>
        <TitleBlock level={3} title="Basic Information" size="medium" />
        {submitError && (
          <UserFormRow>
            <FormHelperText variant="belowField">{submitError}</FormHelperText>
          </UserFormRow>
        )}

        <UserFormRow>
          <Field name="avatar" component={UserAvatarInput} />
        </UserFormRow>

        {hasEmailWarning && <UserEmailAlert isPending={isResendEmailPending} onSend={onEmailResend} />}
        <UserFormRow>
          <div>
            <EmailCheckExistField
              autoFocus={false}
              disabled={!!disabledFields?.email}
              currentEmail={initialValues.email}
              inputVariant={hasEmailWarning ? 'warning_animated_placeholder' : 'animated_placeholder'}
              helperTextVariant="belowField"
              maxLength={60}
            />
            <ButtonLoader
              size="large_long_static"
              color="blue"
              variant={dirty ? 'blue-heavy-border' : 'gray-border'}
              disabled={!dirty}
              isPending={isPending}
              onClick={submit}
            >
              Save Changes
            </ButtonLoader>
          </div>
          <NameField label="Username" name="username" helperTextVariant="belowField" maxLength={60} />
          <DisplayNameField name="displayName" helperTextVariant="belowField" maxLength={60} />
        </UserFormRow>
      </UserFormSection>
    </WrapperUserForm>
  );
};

const UserForm: FC<HOCs & UserFormProps> = ({ initialValues, onAutoSave, ...otherProps }) => {
  const save = useDiffSave<UserFormFields>({ initValues: initialValues, onSave: onAutoSave });

  return (
    <Form initialValues={initialValues} render={renderForm({ onAutoSave: save, ...otherProps })} {...otherProps} />
  );
};

export default UserForm;
